import React, { type ReactElement } from 'react'

type StaticImageData = {
  src: string
}

type AppImageProps = Omit<React.ComponentPropsWithoutRef<'img'>, 'children' | 'ref' | 'src'> & {
  src: string | StaticImageData
  fill?: boolean
  priority?: boolean
  quality?: number
  placeholder?: 'blur' | 'empty'
  blurDataURL?: string
  onLoadingComplete?: (image: HTMLImageElement) => void
  unoptimized?: boolean
}

export default function AppImage({
  src,
  alt,
  fill,
  style,
  onLoadingComplete,
  onLoad,
  priority,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  unoptimized: _unoptimized,
  loading,
  ...props
}: AppImageProps): ReactElement {
  const resolvedSrc = typeof src === 'string' ? src : src?.src || ''
  const mergedStyle = {
    ...(fill
      ? {
          width: '100%',
          height: '100%',
          objectFit: style?.objectFit || 'cover',
          display: 'block',
        }
      : {}),
    ...style,
  }

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    const image = event.currentTarget
    onLoadingComplete?.(image)
    onLoad?.(event)
  }

  return (
    <img
      {...props}
      src={resolvedSrc}
      alt={alt || ''}
      style={mergedStyle}
      loading={loading || (priority ? 'eager' : fill ? 'lazy' : undefined)}
      fetchPriority={priority ? 'high' : props.fetchPriority}
      onLoad={handleLoad}
    />
  )
}
